// Feature: features/nma-payer-response-pipeline.md, features/nma-claims-command-center-insights.md, features/nma-claims-beta-banner-and-blurred-summaries.md, features/nma-claims-performance-and-component-extraction.md
import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  Text,
  Select,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  HStack,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import {
  formatCurrency,
  formatLabel,
  getStatusColorScheme,
  getUrgencyColorScheme,
  getCategoryColorScheme,
} from './shared';

const PAGE_SIZE_OPTIONS = [50, 100, 250];
const DEFAULT_PAGE_SIZE = 100;

const NMAClaimsExplorerTab = React.memo(({
  filteredAndSortedClaims = [],
  totalClaimsCount = 0,
  kpis = {},
  filterOptions = { payers: [], lifecycle_statuses: [] },
  search,
  setSearch,
  payerFilter,
  setPayerFilter,
  claimTypeFilter,
  setClaimTypeFilter,
  statusFilter,
  setStatusFilter,
  cptFilter,
  setCptFilter,
  modifierFilter,
  setModifierFilter,
  quickFilter,
  setQuickFilter,
  hasActiveFilters,
  onResetFilters,
  sortConfig,
  onRequestSort,
  onOpenClaimDetail,
}) => {
  // Client-side pagination avoids mounting ~8,000 <Tr> rows into the DOM at once
  // (see features/nma-claims-performance-and-component-extraction.md)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    payerFilter,
    claimTypeFilter,
    statusFilter,
    cptFilter,
    modifierFilter,
    quickFilter,
    sortConfig,
    pageSize,
  ]);

  const totalMatching = filteredAndSortedClaims.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalMatching);

  const paginatedClaims = useMemo(
    () => filteredAndSortedClaims.slice(startIdx, endIdx),
    [filteredAndSortedClaims, startIdx, endIdx]
  );

  const getSortArrow = (key) => {
    if (sortConfig?.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <>
      {/* Compact Inline Filter Strip for Claims Explorer */}
      <Flex
        gap={2.5}
        wrap="wrap"
        align="center"
        justify="space-between"
        mb={3}
        p={2.5}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Flex gap={2.5} wrap="wrap" align="center">
          <Input
            size="xs"
            bg="white"
            w="220px"
            placeholder="Filter Claim #, Case #, Patient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            size="xs"
            bg="white"
            w="190px"
            value={payerFilter}
            onChange={(e) => setPayerFilter(e.target.value)}
          >
            <option value="">All Payers</option>
            {(filterOptions.payers || []).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
          <Select
            size="xs"
            bg="white"
            w="130px"
            value={claimTypeFilter}
            onChange={(e) => setClaimTypeFilter(e.target.value)}
          >
            <option value="">All (Pro &amp; Tech)</option>
            <option value="pro">Pro (P)</option>
            <option value="tech">Tech (T)</option>
          </Select>
          <Select
            size="xs"
            bg="white"
            w="205px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Lifecycle Statuses</option>
            {(filterOptions.lifecycle_statuses || []).map((st) => (
              <option key={st} value={st}>{formatLabel(st)}</option>
            ))}
          </Select>
          {hasActiveFilters && (
            <Button size="xs" variant="ghost" colorScheme="blue" onClick={onResetFilters}>
              Reset Filters
            </Button>
          )}
        </Flex>

        {/* Compact Top Pagination Summary & Controls */}
        <HStack spacing={2} wrap="wrap">
          <Text fontSize="xs" color="gray.600">
            Showing{' '}
            <b>
              {totalMatching === 0 ? 0 : (startIdx + 1).toLocaleString()}&ndash;{endIdx.toLocaleString()}
            </b>{' '}
            of <b>{totalMatching.toLocaleString()}</b> claims
          </Text>
          <Select
            size="xs"
            bg="white"
            w="105px"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => (
              <option key={sz} value={sz}>{sz} / page</option>
            ))}
          </Select>
          <Button
            size="xs"
            variant="outline"
            bg="white"
            isDisabled={safePage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Text fontSize="xs" fontWeight="semibold" color="gray.700">
            {safePage} / {totalPages}
          </Text>
          <Button
            size="xs"
            variant="outline"
            bg="white"
            isDisabled={safePage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </HStack>
      </Flex>

      {((cptFilter && cptFilter !== 'ALL_CLAIMS') || modifierFilter) && (
        <HStack spacing={2} mb={3} p={2} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
          <Text fontSize="xs" fontWeight="bold" color="blue.800">
            Active Drill-Down Filters:
          </Text>
          {cptFilter && cptFilter !== 'ALL_CLAIMS' && (
            <Badge colorScheme="blue" variant="solid" px={2} py={0.5}>
              CPT: {cptFilter}
              <Button
                size="xs"
                variant="unstyled"
                ml={1.5}
                color="white"
                onClick={() => setCptFilter('')}
              >
                ✕
              </Button>
            </Badge>
          )}
          {modifierFilter && (
            <Badge colorScheme="purple" variant="solid" px={2} py={0.5}>
              Modifier: {modifierFilter}
              <Button
                size="xs"
                variant="unstyled"
                ml={1.5}
                color="white"
                onClick={() => setModifierFilter('')}
              >
                ✕
              </Button>
            </Badge>
          )}
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            onClick={() => {
              setCptFilter('');
              setModifierFilter('');
            }}
          >
            Clear Drill-Down
          </Button>
        </HStack>
      )}

      <HStack spacing={2} mb={4} wrap="wrap">
        {[
          { id: 'ALL', label: `All Claims (${totalClaimsCount})` },
          { id: 'HIGH_URGENCY', label: 'High Urgency / Action Required' },
          { id: 'RECORDS_NEEDED', label: `Pending Medical Records (${kpis.pending_records_count || 0})` },
          { id: 'RECORDS_SENT', label: `Records / Disputes Sent (${kpis.records_sent_count || 0})` },
          { id: 'PARTIALLY_PAID', label: `Partially Paid w/ Denials (${kpis.partially_paid_claims_count || 0})` },
          { id: 'FULL_DENIAL', label: 'Zero-Pay Denials' },
          { id: 'CH_REJECTED', label: `277CA Rejected (${kpis.clearinghouse_rejected_count || 0})` },
          { id: 'PAID', label: `Paid / Deductible (${kpis.paid_claims_count || 0})` },
        ].map((pill) => (
          <Button
            key={pill.id}
            size="xs"
            colorScheme={quickFilter === pill.id ? 'blue' : 'gray'}
            variant={quickFilter === pill.id ? 'solid' : 'outline'}
            onClick={() => setQuickFilter(pill.id)}
          >
            {pill.label}
          </Button>
        ))}
      </HStack>

      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead bg="gray.100">
            <Tr>
              <Th cursor="pointer" onClick={() => onRequestSort('claim_number')}>
                Claim #{getSortArrow('claim_number')}
              </Th>
              <Th cursor="pointer" onClick={() => onRequestSort('nma_case_no')}>
                Case #{getSortArrow('nma_case_no')}
              </Th>
              <Th>Patient / DOS</Th>
              <Th cursor="pointer" onClick={() => onRequestSort('payer_name')}>
                Payer{getSortArrow('payer_name')}
              </Th>
              <Th cursor="pointer" onClick={() => onRequestSort('lifecycle_status')}>
                Lifecycle Status{getSortArrow('lifecycle_status')}
              </Th>
              <Th>Recommended Next Action</Th>
              <Th isNumeric cursor="pointer" onClick={() => onRequestSort('total_billed')}>
                Billed (beta){getSortArrow('total_billed')}
              </Th>
              <Th isNumeric cursor="pointer" onClick={() => onRequestSort('total_paid')}>
                Paid (beta){getSortArrow('total_paid')}
              </Th>
              <Th isNumeric cursor="pointer" onClick={() => onRequestSort('total_denied')}>
                Denied (beta){getSortArrow('total_denied')}
              </Th>
              <Th>Denial Categories / Records</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedClaims.map((row) => (
              <Tr key={`${row.case_id}-${row.claim_number}`} _hover={{ bg: 'gray.50' }}>
                <Td fontWeight="bold">
                  <HStack spacing={2}>
                    <Badge colorScheme={row.claim_type === 'tech' ? 'purple' : 'blue'}>
                      {row.claim_type?.toUpperCase()}
                    </Badge>
                    <Text
                      as="span"
                      color="blue.600"
                      cursor="pointer"
                      textDecoration="underline"
                      onClick={() => onOpenClaimDetail(row)}
                    >
                      {row.claim_number}
                    </Text>
                  </HStack>
                </Td>
                <Td>{row.nma_case_no}</Td>
                <Td>
                  <Text fontSize="xs" fontWeight="semibold">{row.patient_name || 'N/A'}</Text>
                  <Text fontSize="xs" color="gray.500">
                    DOS: {row.date_of_service || 'N/A'} {row.place_of_service ? `(POS ${row.place_of_service})` : ''}
                  </Text>
                </Td>
                <Td maxW="200px">
                  <Text fontSize="xs" fontWeight="medium" noOfLines={1} title={row.payer_name}>
                    {row.payer_name}
                  </Text>
                  <Badge
                    size="xs"
                    variant="subtle"
                    colorScheme={row.clearinghouse_disposition === 'REJECTED' ? 'red' : 'green'}
                    fontSize="10px"
                  >
                    277CA: {row.clearinghouse_disposition}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={getStatusColorScheme(row.lifecycle_status)} fontSize="11px">
                    {formatLabel(row.lifecycle_status)}
                  </Badge>
                </Td>
                <Td maxW="240px">
                  <HStack spacing={1} mb={0.5}>
                    <Badge colorScheme={getUrgencyColorScheme(row.urgency)} fontSize="10px">
                      {row.urgency}
                    </Badge>
                    <Text fontSize="xs" fontWeight="semibold" noOfLines={1}>
                      {formatLabel(row.recommended_action)}
                    </Text>
                  </HStack>
                  <ChakraTooltip label={row.action_details} hasArrow>
                    <Text fontSize="11px" color="gray.600" noOfLines={1}>
                      {row.action_details}
                    </Text>
                  </ChakraTooltip>
                </Td>
                <Td isNumeric fontWeight="medium">{formatCurrency(row.total_billed)}</Td>
                <Td isNumeric fontWeight="bold" color={row.total_paid > 0 ? 'green.600' : 'gray.500'}>
                  {formatCurrency(row.total_paid)}
                </Td>
                <Td isNumeric fontWeight="bold" color={row.total_denied > 0 ? 'red.600' : 'gray.400'}>
                  {formatCurrency(row.total_denied)}
                </Td>
                <Td maxW="220px">
                  <Flex gap={1} wrap="wrap">
                    {(row.denial_categories || []).map((dc) => (
                      <Badge key={dc} colorScheme={getCategoryColorScheme(dc)} fontSize="10px">
                        {formatLabel(dc)}
                      </Badge>
                    ))}
                    {row.records_requested && (
                      <Badge colorScheme={row.records_sent ? 'blue' : 'purple'} variant="solid" fontSize="10px">
                        {row.records_sent ? 'RECORDS SENT' : 'RECORDS NEEDED'}
                      </Badge>
                    )}
                  </Flex>
                </Td>
                <Td>
                  <Button size="xs" colorScheme="blue" variant="outline" onClick={() => onOpenClaimDetail(row)}>
                    View Timeline ({row.timeline_events_count})
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Bottom Pagination Footer */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" mt={4} pt={3} borderTop="1px solid" borderColor="gray.200" wrap="wrap" gap={2}>
          <Text fontSize="xs" color="gray.600">
            Showing{' '}
            <b>
              {totalMatching === 0 ? 0 : (startIdx + 1).toLocaleString()}&ndash;{endIdx.toLocaleString()}
            </b>{' '}
            of <b>{totalMatching.toLocaleString()}</b> matching claims
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              variant="outline"
              isDisabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
              Page {safePage} of {totalPages}
            </Text>
            <Button
              size="xs"
              variant="outline"
              isDisabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </>
  );
});

export default NMAClaimsExplorerTab;
